/**
 * Login page — standalone page (no sidebar/topbar).
 * Redirects to /pulse on success.
 */

import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../lib/auth'
import { Icon } from '../components/Icon'

export function LoginView() {
  const { login } = useAuth()
  const navigate  = useNavigate()
  const { t } = useTranslation()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [showPw,   setShowPw]   = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    const result = await login(email.trim(), password)
    setLoading(false)
    if (result.ok) {
      navigate('/pulse', { replace: true })
    } else {
      setError(result.error)
    }
  }

  return (
    <div className="min-h-screen bg-ink-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        {/* Wordmark */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-xl gradient-hero grid place-items-center text-white font-bold text-lg">B</div>
          <div>
            <div className="text-xl font-bold tracking-tight">
              ByeLabel<span className="text-mint-500">·</span><span className="text-ink-500 font-medium">FinOps</span>
            </div>
            <div className="text-xs text-ink-500">CFO Console</div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-ink-200 p-8">
          <h1 className="text-[17px] font-semibold text-ink-900 mb-6">{t('login.sign_in')}</h1>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">
                {t('login.email_address')}
              </label>
              <input
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full h-10 px-3 bg-ink-50 border border-ink-200 rounded-xl text-sm focus-ring"
                placeholder={t('login.email_placeholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1.5">
                {t('login.password')}
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full h-10 pl-3 pr-10 bg-ink-50 border border-ink-200 rounded-xl text-sm focus-ring"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-2.5 text-ink-400 hover:text-ink-600"
                  tabIndex={-1}
                >
                  <Icon name={showPw ? 'eye-off' : 'eye'} className="w-4 h-4" />
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-flame-700 bg-flame-50 border border-flame-200 rounded-xl px-3 py-2.5">
                <Icon name="alert-circle" className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full h-10 bg-royal-600 text-white font-medium rounded-xl text-sm
                         hover:bg-royal-700 disabled:opacity-50 disabled:cursor-not-allowed
                         transition-colors flex items-center justify-center gap-2"
            >
              {loading && <Icon name="loader-circle" className="w-4 h-4 animate-spin" />}
              {loading ? t('login.signing_in') : t('login.sign_in')}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-ink-400">
            {t('login.no_account')}
          </p>
        </div>

        <p className="text-center text-[11px] text-ink-400 mt-6">
          {t('login.internal_only')}
        </p>
      </div>
    </div>
  )
}
